// src/components/fulfillment/FulfillmentTable.jsx
import React, { useMemo, useRef } from 'react';
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import TableViewport from '../common/TableViewport';
import useContainerWidth from '../common/useContainerWidth';
import useColumnPriority from '../common/useColumnPriority';
import { SP } from '../../theme/tokens';
import { formatMonthLabel } from '../../utils/formatters';
import { formatMoney } from '../../utils/format';

// Hausform statt `Intl.NumberFormat('en-GB')` (Regel-16-Gate B1, 2026-10-06).
const eur = (value) => formatMoney(value);
const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/**
 * Die Spalten der Orders-Tabelle (UI2 Teil 10, G13a-4a).
 *
 * 🔴 **Alle acht sind `kern`.** C3 ist eine **Vergleichstabelle** (§11.1) — die
 * Zeilen werden ueber ihre Kostenspalten verglichen, und eine Spalte, die je
 * Breite verschwindet, nimmt den Vergleich mit. Der schmale Fall loest deshalb
 * ueber den `TableViewport` waagerecht, nicht ueber ausgeblendete Spalten
 * (C3-11). Die Deskriptoren tragen trotzdem ihre `kind`: daran entscheidet
 * `useColumnPriority`, welche Spalten in den Kasten passen, und `TableViewport`
 * meldet daraus sein Spaltenbudget an den Seitenkopf.
 */
const SPALTEN = [
  { key: 'month', priority: 'kern', kind: 'date' },
  { key: 'reference_number', priority: 'kern', kind: 'identity' },
  { key: 'order_number', priority: 'kern', kind: 'text' },
  { key: 'ref_kind', priority: 'kern', kind: 'chip' },
  { key: 'product_count', priority: 'kern', kind: 'number' },
  { key: 'carrier', priority: 'kern', kind: 'text' },
  { key: 'order_country', priority: 'kern', kind: 'text' },
  { key: 'total_net', priority: 'kern', kind: 'number' },
];

/**
 * 🔴 **Die Spaltenbreiten stehen in PX, nicht als Prozentanteil von
 * `COLUMN_MAX`** (Regel-16-Blocker der Runde 1, 2026-10-26).
 *
 * Bis hierher galt `COLUMN_MAX[kind].max / Σ · 100 %`. Fuer DIESE Tabelle war
 * das falsch, und es ist nachrechenbar: Σ der acht `max` ist **2000**, eine
 * `number`-Spalte bekam damit **8 %** — vom Tabellenkasten bei 1280 Viewport
 * (886 px gemessen, s. u.) sind das **71 px**. Davon nimmt MUI `size="small"`
 * 2 × 16 px Polster, es bleiben **39 px** fuer „TOTAL SUM" (rund 61 px bei 0.7rem/600/
 * uppercase + 0.06em Sperrung, das Wurzelmass ist 14 px) und fuer „195,06 €"
 * (rund 64 px). Beides lief in die Nachbarzelle bzw. wurde abgeschnitten — die
 * vier Fotos 1280/1440/1920/2560 zeigen genau das, die Basis (ohne feste
 * Breiten) nicht.
 *
 * 🔴 **Der Kasten ist bei 1280 Viewport 886 px breit, nicht 928** (Regel-16-
 * Runde 2, 2026-10-27). Die 928 vergassen den **Hauptrand**: der Seiteninhalt
 * sitzt in `SidebarAndHeaderBar` › `desktopMainSx` und traegt dort 20 px je
 * Seite.
 *
 * 🔴 **DREI ZAHLEN FUER DENSELBEN KASTEN — und nur eine ist massgeblich**
 * (Regel-16-C1 der Runde 3). Bis hierher standen alle drei nebeneinander, ohne
 * dass eine sagte, welche gilt:
 *
 *     886  im BROWSER gemessen: `clientWidth` des Rollkastens,
 *          `r8_g13a4a4_zweig.log:18` — **massgeblich**
 *     887  am FOTO `1280_grund.png` gemessen (Panel-Kante 951 − 64) —
 *          Zweitbeleg, 1 px ueber der Browsermessung
 *     888  die RECHENKETTE aus den Layoutwerten:
 *            1280  Viewport
 *            − 240 Seitenleiste
 *            −  40 Hauptrand (2 × 20)
 *            −  48 `PAGE_GUTTER` (2 × 24, `PageContainer` ab `sm`)
 *            = 952  Panel-Aussenkante
 *            −  64 Polster (2 × 16 der `Panel`-Innenbox + 2 × 16 dieser Box)
 *            = 888  Tabellenkasten
 *
 * Die Kette ist richtig gerechnet und trifft die Groesse trotzdem nicht: sie
 * nimmt die **Layoutwerte**, und die gerenderte Panel-Aussenkante ist am Foto
 * **951** gemessen — 1 px schmaler als die gerechneten 952. Zwischen Foto
 * (887) und Browser (886) liegt nochmals 1 px; seine Ursache ist hier **nicht
 * gemessen**, und sie muss auch nicht geklaert werden — fuer die Reserve zaehlt
 * der kleinere Wert, und das ist der gemessene Browserwert. Wer hier
 * nachrechnet, rechnet gegen **886**.
 *
 * Deshalb steht die Breite jetzt JE SPALTE, hergeleitet aus dem **Kopf**:
 * `Textbreite + 4 px Abstand + 18 px Sortiersymbol + 2 × 16 px Polster`.
 * Der Inhalt ist die zweite Schranke (Geldwert, Monatslabel, Referenz). Die
 * Summe ist **865 px** und liegt damit unter dem gemessenen Kasten bei 1280
 * (886) — dort rollt die Tabelle NICHT, und die Geldspalte steht samt „€"
 * vollstaendig im Bild. Unter 865 rollt sie im `TableViewport` (C3-11), statt
 * zu quetschen: `minWidth` ist genau diese Summe und nicht mehr
 * Σ `COLUMN_MAX[kind].min`. Die Summe der acht `min` ist **888** — 2 px
 * **ueber** dem gemessenen Kasten, nicht darunter; sie war also keine
 * Entlastung. Und sie ist nicht die Panel-Aussenkante von oben (die ist 952
 * gerechnet / 951 gemessen): es ist eine andere Groesse, die zufaellig
 * denselben Wert hat. Das Quetschen kam auch nicht von dieser Summe, sondern
 * von der **Verteilung**: 8 % fuer die Geldspalte sind 71 px.
 *
 * ⚠️ **Die Reserve, offen gerechnet:** 886 − 865 = **21 px**. Eine klassische
 * 17-px-Bildlaufleiste (Windows; macOS zeichnet sie ueber den Inhalt und nimmt
 * nichts) laesst davon **4 px** uebrig — die Tabelle rollt auch dort nicht, und
 * selbst bei wenigen Pixel Ueberlauf bliebe das „€" stehen, weil die Geldzelle
 * 24 px Innenluft traegt (120 − 96). Enger wird es nur mit aufgeklappter
 * Seitenleiste (`SIDEBAR_WIDTH_MODUS`, 280): Kasten 846 (886 − 40), dann rollt
 * der `TableViewport` bewusst (Kantenhinweis + klebende erste Spalte) — keine
 * Spalte verschwindet dabei.
 *
 * ⚠️ Die Textbreiten sind aus den Schriftmassen gerechnet, nicht einzeln im
 * Browser gemessen; gemessen ist der Kasten (886, `clientWidth`) — und seit dem
 * Lauf g13a4a7 sind es **zwei** Inhalte mehr: die Referenz mit 84,1 px (12
 * Zeichen) und „STORAGE" mit 54,6 px. Die zwei Spalten, an denen die Schranke
 * bisher nur geschaetzt war, tragen ihre Zahl damit aus dem Browser. Sie sind je
 * **Inhalt** aufgerundet; wo der Kopf die Schranke ist (gemessen 84,6 px statt
 * der frueher geschaetzten 88), deckt ihn der Streckfaktor (886/865) — F.5
 * misst jeden Kopf seit NB9. Und die Kopfzelle hat ein zweites Netz:
 * `overflow: hidden` (der Kopf kann nicht mehr in die Nachbarzelle laufen)
 * und `title` mit dem vollen Namen (ein gekuerzter Kopf bleibt erreichbar).
 * Wer eine Spalte ergaenzt, rechnet die Summe gegen den **gemessenen Kasten
 * 886** nach.
 */
const SPALTEN_BREITE_PX = {
  month: 95,             // „MONTH" 41 + 22 + 32 = 95 · Inhalt „Oct 2026" 58 + 32 = 90
  // Referenz: der INHALT ist gemessen (84,1 px, breiteste reale Form, Browserlauf
  // g13a4a7 bei 1280) und braucht 84,1 + 32 = 116,1. 118 steht darueber, und
  // gerendert wird daraus 118 × 886/865 = 120,9 ⇒ 88,9 px Inhalt gegen 84,1
  // ⇒ 4,8 px Luft. Die Schranke dieser Spalte ist damit der KOPF, nicht der
  // Inhalt: der Kopf „REFERENCE" ist im Browser gemessen rund 84,6 px breit
  // (Lauf g13a4a9: Ueberstand des Sortierlabels −4,3 px an der Polsterkante,
  // bei 88,9 px Inhalt) — die frueher hier gerechneten 66 + 22 = 88 px waren
  // rund 3,4 px zu breit. Luft sind damit 4,3 px, nicht 0,9.
  // 🔴 Seit Nachbesserung 9 (NB9) misst F.5
  // (`frontend/tests/fulfillment/tabellen.spec.ts`) im Browser JEDE Kopfzelle,
  // diese hier eingeschlossen (`scrollWidth <= clientWidth` und kein Ueberstand
  // des Sortierlabels). Die 4,3 px sind damit gemessen und bewacht — der Rotweg
  // 118 -> 100 kippt den Kopf „REFERENCE" (gemessen +12,0 px, Lauf g13a4a9).
  reference_number: 118,
  order_number: 105,     // „ORDER #" 51 + 22 + 32 = 105
  // Kind: der INHALT ist gemessen („STORAGE" 54,6 px, Lauf g13a4a7) und braucht
  // 54,6 + 32 = 86,6 — mehr als die 86 hier, weil die 86 erst mit dem
  // Streckfaktor gedeckt sind: 86 × 886/865 = 88,1 ⇒ 56,1 px Inhalt gegen 54,6
  // ⇒ 1,5 px Luft. Die frueher hier gerechneten 49 px waren zu klein.
  // ⚠️ Die 1,5 px haengen am Streckfaktor, nicht an der Spalte: mit klassischer
  // 17-px-Bildlaufleiste (Kasten 869, Faktor 869/865) bleiben rechnerisch 54,4
  // gegen 54,6 = −0,2 px — innerhalb der 0,5-px-Toleranz der Koerperzelle, aber
  // nicht plattformunabhaengig.
  ref_kind: 86,
  product_count: 128,    // „# PRODUCTS" 74 + 22 + 32 = 128
  carrier: 104,          // „CARRIER" 50 + 22 + 32 = 104 · Inhalt „DHL Express" 68 + 32 = 100
  order_country: 109,    // „COUNTRY" 55 + 22 + 32 = 109
  total_net: 120,        // „TOTAL SUM" 66 + 22 + 32 = 120 · Inhalt „3.921,86 €" 64 + 32 = 96
};
// Summe **865** — gegen den gemessenen Kasten bei 1280 (886) gerechnet, Reserve 21 px (s. o.).
// Die Nachbesserung 8 hat nur UMGESCHICHTET (Referenz 122 → 118, Kind 82 → 86), damit
// „STORAGE" passt: −4 + 4 = 0, die Summe und die Reserve 21/4 px sind unveraendert.

/**
 * 🔴 Das zweite Netz gegen den Kopf-Ueberlauf: `overflow: hidden` schneidet
 * einen zu breiten Kopf an der Zellkante ab, statt ihn in die Nachbarzelle
 * laufen zu lassen. Eine Ellipse erreicht den Text NICHT — `TableSortLabel`
 * rendert seine Beschriftung als anonymes Flex-Glied, und `text-overflow`
 * wirkt nur auf Block-Container. Deshalb traegt die Zelle zusaetzlich `title`
 * mit dem vollen Namen (Hausform: `ProductsTable.jsx` › Kopfzelle).
 */
const KOPF_SX = { whiteSpace: 'nowrap', overflow: 'hidden' };


const KOPF = {
  month: 'Month',
  reference_number: 'Reference',
  order_number: 'Order #',
  ref_kind: 'Kind',
  product_count: '# Products',
  carrier: 'Carrier',
  order_country: 'Country',
  total_net: 'Total SUM',
};

/**
 * FulfillmentTable — die Orders-Liste des Fulfillment-Berichts.
 *
 * Sie traegt NUR die Tabelle: Suche, Facetten, Quick-Filter, Zaehler und
 * Seitenwechsel sitzen in der Leiste des Panels (`ListToolbar`), die
 * Sortierung im Kopf und in der Adresse (`useListQuery`). Das ist die
 * Arbeitsteilung der Hausform (§2.1) — vorher lagen Suche, Zaehler, Pills und
 * Seitenwechsel IN dieser Datei, und die Seite hatte zwei Zaehler, von denen
 * der eine die Seite zaehlte.
 *
 * @props rows      die bereits gefilterte, sortierte und auf die Seite
 *                  geschnittene Menge
 * @props loading   erster Aufbau oder Nachladen — zehn Skelettzeilen
 * @props sort      `feld` (aufsteigend) oder `-feld` (absteigend)
 * @props onSort    bekommt den NAECHSTEN Sortierwert (`-feld`/`feld`)
 * @props onRowClick oeffnet den Paket-Drawer zur Zeile
 * @props leer      der Leerzustand als Knoten; er steht IN der Tabelle, damit
 *                  der Kopf stehen bleibt (P33)
 */
export default function FulfillmentTable({
  rows = [],
  loading = false,
  sort = '-total_net',
  onSort,
  onRowClick,
  leer = null,
}) {
  const tabelleRef = useRef(null);
  const { width } = useContainerWidth(tabelleRef);
  const { visible } = useColumnPriority({ columns: SPALTEN, containerWidth: width });

  // Die Sortierachse: `-` heisst absteigend. Genau EINE Spalte traegt
  // `aria-sort` — sie kommt aus `TableCell.sortDirection`, nicht aus dem Label
  // (MUI schreibt das Attribut nur daraus).
  const absteigend = String(sort || '').startsWith('-');
  const sortFeld = absteigend ? String(sort).slice(1) : String(sort || '');
  const richtung = absteigend ? 'desc' : 'asc';

  const naechsteSortierung = (feld) => {
    if (feld === sortFeld) return absteigend ? feld : `-${feld}`;
    return `-${feld}`;
  };

  const sichtbare = visible.length ? visible : SPALTEN;
  // Feste Breiten je Spalte (s. `SPALTEN_BREITE_PX`): `table-layout: fixed`
  // nimmt sie aus dem Kopf. `minWidth` ist ihre SUMME — unter ihr rollt der
  // Viewport, statt zu quetschen, und erst dadurch hat der Kantenhinweis einen
  // Gegenstand.
  const spalten = sichtbare.map((sp) => ({ width: SPALTEN_BREITE_PX[sp.key] }));
  const tabellenMin = sichtbare.reduce((s, sp) => s + (SPALTEN_BREITE_PX[sp.key] ?? 0), 0);

  const sums = useMemo(() => {
    const s = { warehouse: 0, transport: 0, total: 0 };
    rows.forEach((r) => {
      s.warehouse +=
        num(r.picking_net) +
        num(r.packing_net) +
        num(r.loading_net) +
        num(r.materials_net) +
        num(r.fees_net) +
        num(r.returns_net) +
        num(r.storage_net) +
        num(r.other_net);
      s.transport += num(r.transport_net);
      s.total += num(r.total_net);
    });
    return s;
  }, [rows]);

  const sichtbarHat = (key) => sichtbare.some((sp) => sp.key === key);

  return (
    <Box ref={tabelleRef} data-testid="liste-tabelle" sx={{ px: SP.card, pt: SP.card }}>
      <TableViewport
        // Die Fusszeile sitzt IN der Leiste; der Boden bleibt trotzdem, weil
        // `TableFooterSimple` mit `stickyOnMobile={false}` im Fluss steht.
        footerPadPx={0}
        stickyFirstColumn
        edgeHint
        priorities={SPALTEN}
        containerWidth={width}
        columns={spalten}
      >
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tabellenMin }}>
          <TableHead>
            <TableRow>
              {SPALTEN.filter((sp) => sichtbarHat(sp.key)).map((sp) => (
                <TableCell
                  key={sp.key}
                  align={sp.kind === 'number' ? 'right' : 'left'}
                  sx={KOPF_SX}
                  // Der volle Name, auch wenn die Zelle ihn abschneidet (s. `KOPF_SX`).
                  title={KOPF[sp.key]}
                  // 🔴 Genau eine Kopfzelle traegt `aria-sort` (P18): MUI
                  // schreibt das Attribut NUR aus `sortDirection`.
                  sortDirection={sortFeld === sp.key ? richtung : false}
                >
                  <TableSortLabel
                    active={sortFeld === sp.key}
                    direction={sortFeld === sp.key ? richtung : 'asc'}
                    onClick={() => onSort?.(naechsteSortierung(sp.key))}
                  >
                    {KOPF[sp.key]}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody data-testid={loading ? 'liste-skelett' : undefined}>
            {loading
              ? Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={`skel-${i}`}>
                    {sichtbare.map((sp, j) => (
                      <TableCell key={j} align={sp.kind === 'number' ? 'right' : 'left'}>
                        <Skeleton variant="text" width={sp.kind === 'number' ? 80 : 160} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((r) => (
                  <TableRow
                    key={`${r.month}-${r.reference_number}`}
                    hover
                    sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                    onClick={() => onRowClick && onRowClick(r)}
                  >
                    {sichtbarHat('month') && <TableCell>{formatMonthLabel(r.month)}</TableCell>}
                    {sichtbarHat('reference_number') && <TableCell>{r.reference_number}</TableCell>}
                    {sichtbarHat('order_number') && <TableCell>{r.order_number || '—'}</TableCell>}
                    {sichtbarHat('ref_kind') && <TableCell>{r.ref_kind}</TableCell>}
                    {sichtbarHat('product_count') && (
                      <TableCell align="right">{Math.round(r.product_count || 0)}</TableCell>
                    )}
                    {sichtbarHat('carrier') && <TableCell>{r.carrier || '—'}</TableCell>}
                    {sichtbarHat('order_country') && <TableCell>{r.order_country || '—'}</TableCell>}
                    {sichtbarHat('total_net') && (
                      <TableCell align="right"><strong>{eur(r.total_net)}</strong></TableCell>
                    )}
                  </TableRow>
                ))}

            {/* Der Leerzustand steht IN der Tabelle: der Kopf bleibt sichtbar
                (P33) und die Spaltenbreiten springen nicht. */}
            {!loading && rows.length === 0 && leer && (
              <TableRow>
                <TableCell colSpan={sichtbare.length || 1} sx={{ borderBottom: 0 }}>
                  {leer}
                </TableCell>
              </TableRow>
            )}

          </TableBody>

          {/* Die Summenzeile steht im FUSS, nicht im Koerper — zwei Gruende:
              `SEL.zeile` (`tbody tr`) zaehlt die DATENZEILEN, und eine
              Summenzeile darin machte aus „50 je Seite" eine 51; und semantisch
              ist es eine Fusszeile der Tabelle. Sie erscheint NUR mit Zeilen:
              eine „0,00 €"-Summe ueber einer leeren Liste waere die Aussage
              „nichts gekostet" statt „nichts geladen" (Regel 22 §3). */}
          {!loading && rows.length > 0 && (
            <TableFooter>
              <TableRow>
                {/* 🔴 Die Aufschluesselung steht wieder hier (Regel-16-W2 der
                    Runde 1): die Basis zeigte „Warehouse … · Transport … ·
                    Total …", die Schale nur noch „Totals" + Gesamt — obwohl
                    `sums.warehouse`/`sums.transport` weiter gerechnet wurden.
                    Die drei Werte stehen in EINER Zelle ueber die fuehrenden
                    Spalten; das haelt die Geldspalte rechts aussen und den
                    Fuss schmal. */}
                <TableCell
                  colSpan={sichtbarHat('total_net') ? Math.max(1, sichtbare.length - 1) : sichtbare.length}
                >
                  <strong>Totals</strong>
                  {' · '}Warehouse {eur(sums.warehouse)}
                  {' · '}Transport {eur(sums.transport)}
                </TableCell>
                {sichtbarHat('total_net') && (
                  <TableCell align="right"><strong>{eur(sums.total)}</strong></TableCell>
                )}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableViewport>
    </Box>
  );
}
